import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import type { NumberRequest } from "@/features/number-requests/types";
import { useDateFormat, useTranslation } from "@/features/utils/i18n";
import { ArrowSquareOutIcon, PackageIcon } from "@phosphor-icons/react";

/**
 * The parcels we shipped for this request, with their tracking links.
 *
 * Read-only: shipping is ours, and each link is also e-mailed as we add it. The
 * card is here for whoever did not get that e-mail, and only once there is a
 * parcel to show.
 */
export function Deliveries({ request }: { request: NumberRequest }) {
  const { t } = useTranslation("numberRequests");
  const { format } = useDateFormat();
  const deliveries = request.deliveries?.items || [];

  if (!deliveries.length) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("numberRequests.deliveries.title")}</CardTitle>
        <CardDescription>
          {t("numberRequests.deliveries.description")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {deliveries.map((delivery) => (
          <a
            key={delivery.id}
            href={delivery.tracking_url}
            target="_blank"
            rel="noreferrer noopener"
            className="hover:bg-muted/50 flex items-center justify-between gap-3 rounded-lg border p-3"
          >
            <div className="flex min-w-0 items-center gap-2">
              <PackageIcon className="h-4 w-4 shrink-0" />
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">
                  {delivery.label || t("numberRequests.deliveries.parcel")}
                </p>
                <p className="text-muted-foreground text-xs">
                  {t("numberRequests.deliveries.shippedOn", {
                    date: format(delivery.created_at, "PP"),
                  })}
                </p>
              </div>
            </div>
            <span className="text-primary flex shrink-0 items-center gap-1 text-sm">
              {t("numberRequests.deliveries.track")}
              <ArrowSquareOutIcon className="h-4 w-4" />
            </span>
          </a>
        ))}
      </CardContent>
    </Card>
  );
}
